<script lang="ts">
import type { CalendarEventExternal } from '@schedule-x/calendar';
import type { Component, ComponentPublicInstance } from 'vue';
import type { ComponentExposed } from 'vue-component-type-helpers';
import type { CompositePrimaryKey, FieldSelection, Filter, GlobalPrimitiveFieldId, QuerySort } from '#layers/data-model/public';
import type { CalendarEventVariant, CalendarNavigation, CalendarViewMode } from '#layers/listing/constants/calendar';
import type { CalendarDisplayOptions, SharedLayoutEmits, SharedLayoutProps } from '#layers/listing/types/layout';
import type { CalendarRange } from './build-calendar-range-filter';
import type { CalendarItemEvent } from './calendar-events';
import { Temporal } from 'temporal-polyfill';

// Saved views hold only the options the user changed, so every option can be missing.
export interface LayoutCalendarProps extends SharedLayoutProps<Partial<CalendarDisplayOptions>> {
	renderDisplayTemplate: Component;
}

export interface LayoutCalendarEmits extends SharedLayoutEmits {
	'open': [key: CompositePrimaryKey];
	'emptyStateAction': [];
	'resetView': [];
	'update:displayOptions': [displayOptions: Partial<CalendarDisplayOptions>];
}
</script>

<script setup lang="ts">
import { ScheduleXCalendar } from '@schedule-x/vue';
import { createReusableTemplate, refDebounced } from '@vueuse/core';
import { computed, toRef, useTemplateRef, watchEffect } from 'vue';
import { getWeekdayDates, nowInTimezone, resolveTimezone, SortDirection, TIMEZONE_LOCAL, useUserLocale } from '#layers/base/public';
import { useDisplayTemplateFieldSelection, useFieldSelectionForFieldIds } from '#layers/blocks/public';
import { useItems } from '#layers/collection-query/public';
import { CollectionReads, getCollectionPrimaryKeyFields, getDatetimePrimitiveSemantics, getTypename, isEmptyFilter, mergeAnd, mergeFieldSelections, resolveDatetimeTimezone, usePrimitiveField, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { injectListingContext } from '#layers/listing/components/Listing/context';
import ListingErrorState from '#layers/listing/components/Listing/ErrorState.vue';
import { useCalendarDateFieldGate } from '#layers/listing/composables/use-calendar-date-field-gate';
import { exposeListingQueryToBlockRootContext } from '#layers/listing/composables/use-listing-query-block-root-context';
import { useSelection } from '#layers/listing/composables/use-selection';
import { useSortGate } from '#layers/listing/composables/use-sort-gate';
import { CALENDAR_PAGE_SIZE, CALENDAR_VIEW_DEFINITIONS, CalendarEventVariants, CalendarNavigations, CalendarViewModes } from '#layers/listing/constants/calendar';
import { buildCalendarRangeFilter } from './build-calendar-range-filter';
import { isSpanning, toCalendarItemEvents } from './calendar-events';
import LayoutCalendarDisplayOptions, { initializeLayoutCalendarOptions } from './LayoutCalendarDisplayOptions.vue';
import LayoutCalendarEventContent from './LayoutCalendarEventContent.vue';
import LayoutCalendarHeader from './LayoutCalendarHeader.vue';
import LayoutCalendarList from './List.vue';
import LayoutCalendarMonthGridDate from './MonthGridDate.vue';
import { useCalendarPartialResults } from './use-calendar-partial-results';
import { useScheduleXCalendar } from './use-schedule-x-calendar';
import { useTimeGridScroll } from './use-time-grid-scroll';
import LayoutCalendarYear from './Year.vue';
import './LayoutCalendar.css';

const {
	collection: collectionProp,
	filter: filterProp,
	displayOptions: displayOptionsProp,
	selectionKeys,
	projectionKeys,
	searchTerm,
} = defineProps<LayoutCalendarProps>();
const emit = defineEmits<LayoutCalendarEmits>();

const collection = toRef(() => collectionProp);
const filter = toRef(() => filterProp);

const { setDisplayOptionsComponent } = injectListingContext();
setDisplayOptionsComponent(LayoutCalendarDisplayOptions);

const dateFieldGate = useCalendarDateFieldGate(collection);
const workspaceSchema = useWorkspaceSchemaStore();
const { formatLocale } = useUserLocale();

const displayOptions = computed(() =>
	initializeLayoutCalendarOptions({ collection: collectionProp, options: displayOptionsProp, allowsStartField: dateFieldGate.allowsStartField, querySchema: workspaceSchema.querySchema }),
);

// Saved date fields the read no longer returns or filters by place no events and send no range.
const configuredStartField = usePrimitiveField(() => displayOptions.value.startDateField);
const configuredEndField = usePrimitiveField(() => displayOptions.value.endDateField);
const startField = computed(() => configuredStartField.value && dateFieldGate.allowsStartField(configuredStartField.value) ? configuredStartField.value : undefined);
const endField = computed(() => configuredEndField.value && dateFieldGate.allowsEndField(configuredEndField.value, startField.value) ? configuredEndField.value : undefined);
const titleTemplate = computed(() => displayOptions.value.titleTemplate);

// Places, counts and fetches items in the start field's display zone, as the table shows it.
const calendarTimeZone = computed(() => {
	const field = startField.value;
	// Uses the viewer's zone for date and datetime fields, which carry no zone, so today is the viewer's day.
	if (!field || !getDatetimePrimitiveSemantics(getTypename(field.type)).isInstantType) return resolveTimezone(TIMEZONE_LOCAL);

	const defaultTimezone = field.meta?.typeOptions?.defaultTimezone;
	return resolveDatetimeTimezone(true, typeof defaultTimezone === 'string' ? defaultTimezone : undefined);
});

function todayInCalendarZone() {
	return nowInTimezone(calendarTimeZone.value).toPlainDate();
}

// Reads today once per zone, so a calendar left open past midnight keeps the previous day as today.
const today = computed(() => todayInCalendarZone());

// Converts the saved 0 for Sunday and 1 for Monday to ISO weekdays, where 7 is Sunday.
const weekStartsOn = computed<1 | 7>(() => displayOptions.value.firstDayOfWeek === 0 ? 7 : 1);

const currentView = computed({
	get: () => displayOptions.value.viewMode,
	set: (viewMode) => saveDisplayOptions({ viewMode }),
});

const currentViewDefinition = computed(() => CALENDAR_VIEW_DEFINITIONS[currentView.value]);
const isPeriodView = computed(() => currentViewDefinition.value.periodMonths !== null);
const isTimeGridView = computed(() => currentView.value === CalendarViewModes.Week || currentView.value === CalendarViewModes.Day);

// Keeps the focused date stable while other options change, so List does not jump to the top.
const currentDate = computed(() => displayOptions.value.currentDate);

const focusedDate = computed<Temporal.PlainDate>({
	get: () => tryParseDate(currentDate.value) ?? today.value,
	set: (date) => {
		if (!date.equals(focusedDate.value)) saveDisplayOptions({ currentDate: date.toString() });
	},
});

const scheduleXCalendar = useTemplateRef<ComponentPublicInstance>('scheduleXCalendar');

const { calendarApp, calendarKey, range: scheduleXRange } = useScheduleXCalendar({
	view: () => currentViewDefinition.value.scheduleXView,
	date: focusedDate,
	weekStartsOn,
	timeZone: calendarTimeZone,
	locale: formatLocale,
	onEventClick: (calendarEvent) => {
		const event = findCalendarItemEvent(calendarEvent);
		if (event) emit('open', event.primaryKey);
	},
	onClickPlusEvents: openDay,
});

// Covers whole months in Year and List, where schedule-x's hidden month grid would pad the range.
const visibleRange = computed<CalendarRange>(() => {
	const { periodMonths } = currentViewDefinition.value;
	if (!periodMonths) return scheduleXRange.value;

	const date = focusedDate.value;
	const start = periodMonths === 12 ? date.with({ month: 1, day: 1 }) : date.with({ day: 1 });
	return { start, end: start.add({ months: periodMonths }).subtract({ days: 1 }) };
});

const mergedFilter = computed<Filter>(() => {
	if (!startField.value) return filter.value;

	const rangeFilter = buildCalendarRangeFilter({ startField: startField.value, range: visibleRange.value, endField: endField.value, timeZone: calendarTimeZone.value });
	if (!rangeFilter) return filter.value;
	if (isEmptyFilter(filter.value)) return rangeFilter;
	return mergeAnd(filter.value, rangeFilter);
});

const titleFieldSelection = useDisplayTemplateFieldSelection(collection, CollectionReads.Many, () => titleTemplate.value ?? undefined);

const dateFieldIds = computed<GlobalPrimitiveFieldId[]>(() =>
	[startField.value?.id, endField.value?.id].filter((id): id is GlobalPrimitiveFieldId => !!id),
);
const dateFieldsSelection = useFieldSelectionForFieldIds(collection, dateFieldIds);

const selection = defineModel<CompositePrimaryKey[]>('selection', { default: () => [] });
const primaryKeyFields = computed(() => getCollectionPrimaryKeyFields(collection.value));
const { selectionFields } = useSelection({
	selection,
	primaryKeyFields,
	selectionKeys: () => selectionKeys,
	projectionKeys: () => projectionKeys,
});

const querySelection = computed<FieldSelection>(() =>
	mergeFieldSelections(titleFieldSelection.value, dateFieldsSelection.value, { fields: [...primaryKeyFields.value, ...selectionFields.value].map((field) => field.apiName) }),
);

const sortGate = useSortGate(collection);

function sortsAscending(field: { id: GlobalPrimitiveFieldId }): boolean {
	return sortGate.directions(field.id).includes(SortDirection.Ascending);
}

// Sorts earliest first, so a range with more than one page is complete up to the last loaded start.
// Leaves out sorts the read does not accept, because a connector's read rejects the whole query for one.
const querySort = computed<QuerySort | undefined>(() => {
	const field = startField.value;
	if (!field || !sortsAscending(field)) return;

	const keys = primaryKeyFields.value.filter((key) => sortsAscending(key));
	return [field, ...keys].map((sortField): QuerySort[number] => ({ [sortField.apiName]: { direction: SortDirection.Ascending } }));
});

// The search input emits on every keystroke, so coalesce it into a single query
// change rather than fetching per keystroke.
const debouncedSearch = refDebounced(computed(() => searchTerm), 300);

const { items, error, refresh, isSettled, totalItemsCount, query: itemsQuery } = useItems({
	collection,
	query: () => ({ filter: mergedFilter.value, ...querySelection.value, search: debouncedSearch.value, sort: querySort.value }),
	pageSize: CALENDAR_PAGE_SIZE,
	// The calendar's query only changes on discrete view/range switches, so throttling
	// would just delay the fetch behind the 500ms window without coalescing anything.
	throttle: false,
});

exposeListingQueryToBlockRootContext(() => ({
	collection: collection.value,
	query: itemsQuery.value,
}));

const calendarEvents = computed<CalendarItemEvent[]>(() => {
	if (!startField.value) return [];

	return toCalendarItemEvents({
		items: items.value,
		collection: collection.value,
		startField: startField.value,
		endField: endField.value,
		titleTemplate: titleTemplate.value,
		querySchema: workspaceSchema.querySchema,
		timeZone: calendarTimeZone.value,
		// Reads now only when the items recompute, so an item that starts or ends on screen updates on the next load.
		now: nowInTimezone(calendarTimeZone.value),
	});
});

const calendarEventsById = computed(() => new Map(calendarEvents.value.map((event) => [event.id, event])));

// Skips drawing items in Year and List, where schedule-x's grid is hidden.
watchEffect(() => {
	calendarApp.value.events.set(isPeriodView.value ? [] : calendarEvents.value);
});

const { notice: partialResultsNotice } = useCalendarPartialResults({
	loaded: () => items.value.length,
	total: totalItemsCount,
	isSettled,
	isSortedByStart: () => querySort.value !== undefined,
	events: calendarEvents,
	range: visibleRange,
});

useTimeGridScroll({
	root: scheduleXCalendar,
	isActive: isTimeGridView,
	isSettled,
	events: calendarEvents,
	range: scheduleXRange,
});

const dayNames = computed(() => getWeekdayDates(weekStartsOn.value).map((date) => date.toLocaleString(formatLocale.value, { weekday: 'short' })));

const [DefineEventContent, ReuseEventContent] = createReusableTemplate<{ calendarEvent: CalendarItemEvent | undefined; variant: CalendarEventVariant }>();

// Looks up Studio's event by id, because schedule-x hands back its own loosely typed copy.
function findCalendarItemEvent(calendarEvent: CalendarEventExternal): CalendarItemEvent | undefined {
	return calendarEventsById.value.get(String(calendarEvent.id));
}

function getMonthGridVariant(calendarEvent: CalendarEventExternal): CalendarEventVariant {
	const event = findCalendarItemEvent(calendarEvent);
	return event && isSpanning(event) ? CalendarEventVariants.Bar : CalendarEventVariants.Dot;
}

function tryParseDate(value: string | undefined) {
	if (!value) return;

	try {
		return Temporal.PlainDate.from(value);
	}
	catch {}
}

function saveDisplayOptions(patch: Partial<CalendarDisplayOptions>) {
	// Spreads the raw prop, so the defaults that `displayOptions` fills in stay out of the saved view.
	emit('update:displayOptions', { ...displayOptionsProp, ...patch });
}

const periodView = useTemplateRef<ComponentExposed<typeof LayoutCalendarYear | typeof LayoutCalendarList>>('periodView');

// Brings the date into view, unlike previous and next, which keep the scroll.
function showDate(date: Temporal.PlainDate) {
	focusedDate.value = date;
	periodView.value?.scrollToDate(date);
}

function getNavigationTarget(navigation: CalendarNavigation): Temporal.PlainDate {
	const { step } = currentViewDefinition.value;

	switch (navigation) {
		case CalendarNavigations.Previous:
			return focusedDate.value.subtract({ [step]: 1 });
		case CalendarNavigations.Next:
			return focusedDate.value.add({ [step]: 1 });
		case CalendarNavigations.Today:
			return todayInCalendarZone();
	}
}

function navigate(navigation: CalendarNavigation) {
	const target = getNavigationTarget(navigation);

	if (navigation === CalendarNavigations.Today) showDate(target);
	else focusedDate.value = target;
}

const header = useTemplateRef<InstanceType<typeof LayoutCalendarHeader>>('header');

// Saves the view and the date in one payload, because a second save would spread stale props.
function openView(view: CalendarViewMode, date: Temporal.PlainDate) {
	saveDisplayOptions({ viewMode: view, currentDate: date.toString() });
	header.value?.focusTitle();
}

function openDay(date: Temporal.PlainDate) {
	openView(CalendarViewModes.Day, date);
}

function openMonth(month: Temporal.PlainYearMonth) {
	openView(CalendarViewModes.Month, month.toPlainDate({ day: 1 }));
}
</script>

<template>
	<div class="flex h-full flex-col overflow-hidden">
		<div
			v-if="!startField"
			class="flex flex-1 items-center justify-center text-sm text-muted"
		>
			{{ $t('layout.calendar.no_start_date_field') }}
		</div>

		<!-- Ahead of the calendar: cached events answer whichever query last succeeded, so leaving them up would present them as the result of the one that just failed. -->
		<ListingErrorState
			v-else-if="error"
			:error
			:can-reset-view
			@retry="refresh"
			@reset-view="$emit('resetView')"
		/>

		<div
			v-else
			data-calendar
			:data-view="currentView"
			class="flex flex-1 flex-col overflow-hidden"
		>
			<LayoutCalendarHeader
				ref="header"
				v-model="currentView"
				:range="visibleRange"
				:focused-date
				:first-day-of-week="displayOptions.firstDayOfWeek"
				:loading="!isSettled"
				@navigate="navigate"
				@pick-date="showDate"
			/>

			<DNotice
				v-if="partialResultsNotice"
				color="neutral"
				orientation="horizontal"
				:description="partialResultsNotice"
				class="mx-6 mb-1.5 w-auto py-1.5"
				:ui="{ description: 'text-xs text-muted', icon: 'size-4 text-muted' }"
			/>

			<div
				v-if="currentView === CalendarViewModes.Month"
				class="flex px-px text-muted"
			>
				<span
					v-for="name in dayNames"
					:key="name"
					class="flex-1 px-2.5 py-1 text-start text-xs"
				>
					{{ name }}
				</span>
			</div>

			<DefineEventContent v-slot="{ calendarEvent, variant }">
				<LayoutCalendarEventContent
					v-if="calendarEvent"
					:calendar-event
					:variant
					:title-template
					:collection-id="collection.id"
					:render-display-template
				/>
			</DefineEventContent>

			<LayoutCalendarList
				v-if="currentView === CalendarViewModes.List"
				ref="periodView"
				:range="visibleRange"
				:focused-date
				:events="calendarEvents"
				:time-zone="calendarTimeZone"
				:today
				:is-settled
				:empty-state
				@open="$emit('open', $event.primaryKey)"
				@empty-state-action="$emit('emptyStateAction')"
			>
				<template #event="{ calendarEvent }">
					<ReuseEventContent
						:calendar-event
						:variant="CalendarEventVariants.Row"
					/>
				</template>
			</LayoutCalendarList>

			<LayoutCalendarYear
				v-else-if="currentView === CalendarViewModes.Year"
				ref="periodView"
				:range="visibleRange"
				:focused-date
				:events="calendarEvents"
				:today
				:week-starts-on
				@select-day="openDay"
				@select-month="openMonth"
			/>

			<ClientOnly>
				<!-- Hides schedule-x in Year and List instead of unmounting it, so going back does not rebuild it. -->
				<ScheduleXCalendar
					v-show="!isPeriodView"
					ref="scheduleXCalendar"
					:key="calendarKey"
					:calendar-app
				>
					<template #headerContent>
						<span />
					</template>
					<template #monthGridDate="{ jsDate }">
						<LayoutCalendarMonthGridDate
							:js-date
							:today
							@open="openDay"
						/>
					</template>
					<template #timeGridEvent="{ calendarEvent }">
						<ReuseEventContent
							:calendar-event="findCalendarItemEvent(calendarEvent)"
							:variant="CalendarEventVariants.Block"
						/>
					</template>
					<template #dateGridEvent="{ calendarEvent }">
						<ReuseEventContent
							:calendar-event="findCalendarItemEvent(calendarEvent)"
							:variant="CalendarEventVariants.AllDay"
						/>
					</template>
					<template #monthGridEvent="{ calendarEvent }">
						<ReuseEventContent
							:calendar-event="findCalendarItemEvent(calendarEvent)"
							:variant="getMonthGridVariant(calendarEvent)"
						/>
					</template>
				</ScheduleXCalendar>
			</ClientOnly>
		</div>
	</div>
</template>
